/**
 * 束ね請求の窓（#266、docs/96 決めごと14）。同じ{customer}・同じ受注（見積なしは同じ{project}）の発行済・未請求の{deliveryNote}を選んで1枚の請求書に。
 *   明細の形：全行（{deliveryNote}ごとに「日付 納品 番号」の見出しと明細）／1枚1行（{deliveryNote}1枚を1行）。どちらも明細に納品日と番号が出る。
 *   作ったら請求書の画面へ。同じ受注に請求書の下書きがあれば、その下書きを開く（409 INVOICE_DRAFT_EXISTS）
 */
import { BUNDLE_MODE_LABEL, DELIVERY_NOTE_MSG, type BundleMode, type DeliveryNoteList, type Invoice } from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";
import { api, ApiError, messageOf } from "../../api.js";
import { Segmented } from "../../components/Segmented.jsx";
import { ErrorText, Help } from "../../components/ui.jsx";
import { t } from "../../labels.js";
import { md, yen } from "../../lib/num.js";

export function BundleInvoiceDialog({ seed, preselect, onClose }: { seed: { id: string; sourceQuote: { id: string } | null; projectGroup: { id: string } | null }; preselect?: string[]; onClose: () => void }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const scope = seed.sourceQuote ? `quote=${seed.sourceQuote.id}` : seed.projectGroup ? `group=${seed.projectGroup.id}` : "";
  const list = useQuery({ queryKey: ["delivery-notes", "bundle", scope], queryFn: () => api<DeliveryNoteList>("GET", `/delivery-notes?tab=issued&${scope}`) });
  const seedKey = list.data?.items.find((x) => x.id === seed.id)?.billKey;
  const items = (list.data?.items ?? []).filter((x) => x.billKey === seedKey);
  // 一覧で選んだものがあれば、それだけ選んだ形で始める
  const [offSet, setOff] = useState<Set<string> | null>(null);
  const off = offSet ?? new Set(preselect ? items.filter((x) => !preselect.includes(x.id)).map((x) => x.id) : []);
  const [mode, setMode] = useState<BundleMode>("lines");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const picked = items.filter((x) => !off.has(x.id));
  const create = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const inv = await api<Invoice>("POST", "/invoices", { fromDeliveryNoteIds: picked.map((x) => x.id), bundleMode: mode });
      void qc.invalidateQueries({ queryKey: ["delivery-notes"] });
      void qc.invalidateQueries({ queryKey: ["invoices"] });
      void qc.invalidateQueries({ queryKey: ["home"] });
      nav(`/invoices/${inv.id}`, { state: { fresh: true, notice: t(DELIVERY_NOTE_MSG.invoiceFromNotes).replace("{n}", String(picked.length)) } });
    } catch (e) {
      if (e instanceof ApiError && typeof e.problem.invoiceId === "string") {
        nav(`/invoices/${e.problem.invoiceId}`);
        return;
      }
      setBusy(false);
      setError(messageOf(e));
    }
  };
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="dlg" role="dialog" aria-label={DELIVERY_NOTE_MSG.makeInvoiceTitle} onKeyDown={(e) => e.key === "Escape" && !busy && onClose()} data-testid="dn-bundle-dialog">
        <div className="dh">{DELIVERY_NOTE_MSG.makeInvoiceTitle}</div>
        <div className="db">
          <p className="muted" style={{ margin: 0 }}>{t(DELIVERY_NOTE_MSG.bundleSame)}</p>
          {list.isPending ? (
            <p className="hint">読み込んでいます…</p>
          ) : (
            <ul className="dn-bundle" data-testid="dn-bundle-list">
              {items.map((x) => (
                <li key={x.id}>
                  <label>
                    <input type="checkbox" checked={!off.has(x.id)} disabled={busy} onChange={(e) => setOff(() => { const n = new Set(off); if (e.target.checked) n.delete(x.id); else n.add(x.id); return n; })} data-testid={`dn-bundle-${x.documentNo}`} />
                    <span className="num">{x.documentNo}</span>
                    <span className="muted">{md(x.issueDate)} 納品</span>
                    <span>{x.subject}</span>
                    <b className="num">{yen(x.subtotal)} 円</b>
                  </label>
                </li>
              ))}
            </ul>
          )}
          <div className="row" style={{ alignItems: "center", gap: 12 }}>
            <span id="dn-mode-l">{DELIVERY_NOTE_MSG.bundleMode}</span>
            <Segmented labelledBy="dn-mode-l" options={(["lines", "perNote"] as const).map((v) => ({ value: v, label: BUNDLE_MODE_LABEL[v], testId: `dn-mode-${v}` }))} value={mode} onChange={setMode} disabled={busy} />
            <Help label={DELIVERY_NOTE_MSG.bundleMode} text={t(DELIVERY_NOTE_MSG.bundleHelp)} testId="help-dn-mode" />
          </div>
          <ErrorText>{error}</ErrorText>
        </div>
        <div className="df">
          <span className="sp" />
          <button type="button" className="btn quiet" disabled={busy} onClick={onClose}>取消</button>
          <button type="button" className="btn primary" disabled={busy || !picked.length} onClick={() => void create()} data-testid="dn-bundle-create">
            {DELIVERY_NOTE_MSG.create2}
          </button>
        </div>
      </div>
    </div>
  );
}
